import {useCallback, useId, useSyncExternalStore} from 'react';

/**
 * One hovered row at a time across the page (rail rows, cards).
 *
 * Per-row onMouseEnter/Leave state can strand a row "hovered" when the pointer
 * passes through a portaled tooltip (e.g. Truncate's) and then onto another row
 * — React never fires the first row's mouseleave. A single shared key means
 * entering any row replaces the previous one.
 */
let hoveredKey: string | null = null;
const listeners = new Set<() => void>();

const setHoveredKey = (next: string | null) => {
  if (hoveredKey === next) return;
  hoveredKey = next;
  listeners.forEach((listener) => listener());
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

// Keyed per component instance, so the same workstream shown twice (e.g. under
// My and Team) still hovers independently.
export const useExclusiveHover = () => {
  const key = useId();
  const hovered = useSyncExternalStore(subscribe, () => hoveredKey === key);
  const onMouseEnter = useCallback(() => setHoveredKey(key), [key]);
  const onMouseLeave = useCallback(() => {
    if (hoveredKey === key) setHoveredKey(null);
  }, [key]);
  return {hovered, onMouseEnter, onMouseLeave};
};
